import { useUIStore } from '@/config/stores/ui-store';
import { useConversationMembers } from '@/features/conversation';
import { MessageListItemForward, MessageListItemReply } from '@/features/message-list';
import { chatEE } from '@/layout';
import { useUserById } from '@/shared/hooks/use-user-by-id';
import type { AvatarSize, TimestampStrategy } from '@/shared/theme/message-layout';
import { cn } from '@/shared/utilities';
import { getMessageBorderRadius } from '@/shared/utilities/message-layout-helpers';
import { useIsMobile, useResponsiveMessageSpacing } from '@/shared/utilities/responsive-spacing';
import { MoreVert, Reply } from '@mui/icons-material';
import { Stack, type SxProps, type Theme, Typography } from '@mui/material';
import type { Dayjs } from 'dayjs';
import { type KeyboardEvent, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLongPress } from '../../hooks/use-long-press';
import { useMessageHeader } from '../../hooks/use-message-header';
import { useMessageSwipe } from '../../hooks/use-swipe-gesture';
import type { Message } from '../../models/message';
import { MessageMenuWrapper } from '../message-menu/message-menu-wrapper';
import { MessageQuickActions } from '../message-quick-actions/message-quick-actions';
import { MessageStatusIcon } from '../message-status-icon/message-status-icon';
import { TimestampDisplay } from '../timestamp-display/timestamp-display';
import { MessageLayoutAvatar } from './message-layout.avatar';
import { MessageContent } from './message-layout.content';
import { MessageMarker } from './message-layout.marker';
import { MessageReactions } from './message-layout.reactions';

interface MessageLayoutProps {
  isOwn?: boolean;
  children?: React.ReactNode;
  senderId?: string;
  sentAt?: Dayjs | Date | string | number | undefined;
  showAvatar?: boolean;
  className?: string;
  variant?: 'outlined' | 'elevation';
  message: Message;
  mode?: 'compact' | 'comfortable';
  onContentContextMenu?: (event: React.MouseEvent, message: Message) => void;
  sxOverrides?: SxProps<Theme>;
  isLast?: boolean;
  /** For keyboard nav tracking - position in message list */
  messageIndex?: number;
  /** Total messages count for aria-setsize */
  totalMessages?: number;
  /** Keyboard navigation callback */
  onNavigate?: (direction: 'up' | 'down') => void;
  /** Configurable avatar size */
  avatarSize?: AvatarSize;
  /** Timestamp display strategy */
  timestampStrategy?: TimestampStrategy;
}

const useMessageContextMenu = (message?: Message) => {
  const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number } | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (message) {
        setSelectedMessage(message);
        setContextMenu({
          mouseX: event.clientX + 2,
          mouseY: event.clientY - 6
        });
      }
    },
    [message]
  );

  const handleClose = useCallback(() => {
    setContextMenu(null);
    setSelectedMessage(null);
  }, []);

  return { contextMenu, selectedMessage, handleContextMenu, handleClose };
};

export const MessageLayout = memo<MessageLayoutProps>(
  ({
    isOwn = true,
    children,
    senderId,
    sentAt,
    className,
    variant = 'outlined',
    message,
    mode = 'comfortable',
    onContentContextMenu,
    sxOverrides,
    isLast,
    messageIndex,
    totalMessages,
    onNavigate,
    avatarSize,
    timestampStrategy = 'inline'
  }) => {
    const { t } = useTranslation();
    // Optimize store subscription with selective selector
    const activeConversation = useUIStore((state) => state.activeConversation);
    const { data: members } = useConversationMembers(activeConversation?.conversationId);
    const { contextMenu, selectedMessage, handleContextMenu, handleClose } = useMessageContextMenu(message);

    const reply = message?.parentMessage;
    const isCompact = mode === 'compact';

    // Memoize expensive computations
    const user = useUserById(members || [], senderId);
    const replyUser = useUserById(members || [], reply?.senderId);

    const handleReplyClick = useCallback(() => {
      if (reply) {
        chatEE.emit('zoom-to-message', { conversationId: reply.conversationId!, messageId: reply.messageId! });
      }
    }, [reply]);

    const handleContentContext = useCallback(
      (e: React.MouseEvent) => {
        handleContextMenu(e);
        onContentContextMenu?.(e, message!);
      },
      [handleContextMenu, onContentContextMenu, message]
    );

    /** Keyboard navigation handler for accessibility */
    const handleKeyDown = useCallback(
      (e: KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
          case 'ArrowUp':
            e.preventDefault();
            onNavigate?.('up');
            break;
          case 'ArrowDown':
            e.preventDefault();
            onNavigate?.('down');
            break;
          case 'Enter':
          case ' ': {
            e.preventDefault();
            // Simulate context menu at element center for keyboard activation
            const rect = e.currentTarget.getBoundingClientRect();
            const syntheticEvent = {
              preventDefault: () => {},
              clientX: rect.left + rect.width / 2,
              clientY: rect.top + rect.height / 2
            } as React.MouseEvent;
            handleContextMenu(syntheticEvent);
            break;
          }
        }
      },
      [onNavigate, handleContextMenu]
    );

    // Use extracted header hook instead of inline useMemo
    const headerContent = useMessageHeader({
      sentAt,
      hideName: message?.hideName,
      isOwn,
      user
    });

    // Use helper for border radius calculation
    const contentSxOverrides = useMemo(
      () => ({
        ...sxOverrides,
        ...getMessageBorderRadius(isOwn, !!message?.hideName, !!message?.hideAvatar)
      }),
      [sxOverrides, isOwn, message?.hideName, message?.hideAvatar]
    );

    // Extract grid columns class for readability
    const gridColumns = isOwn ? 'mex:grid-cols-[1fr_auto]' : 'mex:grid-cols-[auto_1fr]';

    // Responsive spacing for mobile/tablet/desktop
    const spacing = useResponsiveMessageSpacing(!!message?.hideAvatar);
    const isMobile = useIsMobile();
    const setReplyMessage = useUIStore((state) => state.setReplyMessage);

    // Swipe gesture for reply (right) and actions (left)
    const handleSwipeReply = useCallback(() => {
      if (message) {
        setReplyMessage(message.conversationId, message);
      }
    }, [message, setReplyMessage]);

    const handleSwipeShowActions = useCallback(() => {
      // Open context menu at message center
      handleContextMenu({
        preventDefault: () => {},
        clientX: window.innerWidth / 2,
        clientY: window.innerHeight / 2
      } as React.MouseEvent);
    }, [handleContextMenu]);

    const {
      handlers: swipeHandlers,
      swipeOffset,
      isSwiping
    } = useMessageSwipe({
      onSwipeRight: handleSwipeReply,
      onSwipeLeft: handleSwipeShowActions,
      enabled: isMobile
    });

    // Long-press for mobile context menu
    const { pressing, handlers: longPressHandlers } = useLongPress({
      onLongPress: (e) => {
        handleContextMenu(e as unknown as React.MouseEvent);
      }
    });

    return (
      <div
        {...swipeHandlers}
        {...(isMobile ? longPressHandlers : {})}
        className={cn(
          'mex:relative mex:col-2 mex:row-auto mex:mx-4 mex:grid mex:gap-x-2 mex:gap-y-0.5',
          'focus:mex:outline focus:mex:outline-2 focus:mex:outline-blue-600 focus:mex:outline-offset-2',
          gridColumns,
          pressing && 'mex:bg-black/5'
        )}
        style={{
          marginBottom: spacing,
          transform: swipeOffset !== 0 ? `translateX(${swipeOffset}px)` : undefined,
          transition: isSwiping ? 'none' : 'transform 0.2s ease-out, background-color 0.1s ease'
        }}
        role='article'
        aria-label={headerContent?.ariaLabel || t('messages.aria.message')}
        aria-posinset={messageIndex !== undefined ? messageIndex + 1 : undefined}
        aria-setsize={totalMessages}
        onKeyDown={handleKeyDown}
        data-message-id={message?.messageId}
        data-message-own={isOwn}
        data-pressing={pressing}
      >
        {/* Swipe visual indicator - reply (right swipe) */}
        {isMobile && swipeOffset > 50 && (
          <div
            className='mex:-translate-y-1/2 mex:pointer-events-none mex:absolute mex:top-1/2 mex:left-0 mex:z-10 mex:flex mex:h-10 mex:w-10 mex:items-center mex:justify-center mex:rounded-full mex:bg-primary-main mex:text-white mex:shadow-md'
            aria-hidden='true'
          >
            <Reply sx={{ fontSize: 20 }} />
          </div>
        )}
        {/* Swipe visual indicator - actions (left swipe) */}
        {isMobile && swipeOffset < -50 && (
          <div
            className='mex:-translate-y-1/2 mex:pointer-events-none mex:absolute mex:top-1/2 mex:right-0 mex:z-10 mex:flex mex:h-10 mex:w-10 mex:items-center mex:justify-center mex:rounded-full mex:bg-gray-600 mex:text-white mex:shadow-md'
            aria-hidden='true'
          >
            <MoreVert sx={{ fontSize: 20 }} />
          </div>
        )}
        <MessageMarker message={message} className='mex:col-span-2' />
        <MessageLayoutAvatar
          className={cn('mex:col-span-1 mex:row-span-1', isOwn ? ' mex:order-2' : 'mex:order-1')}
          peerId={senderId || ''}
          src={user?.profile.avatarRef || undefined}
          name={user?.fullName}
          isCompact={isCompact}
          hideAvatar={message?.hideAvatar}
          isOwn={isOwn}
          avatarSize={avatarSize}
        />
        <Stack
          className={cn('mex:group mex:relative mex:col-span-1', isOwn ? 'mex:order-1' : 'mex:order-2', className)}
          direction={isOwn ? 'row-reverse' : 'row'}
          gap={1}
        >
          <Stack
            className={cn('mex:flex mex:flex-col mex:gap-1')}
            sx={{
              maxWidth: isCompact ? 'calc(100% - 48px)' : { xs: '85%', sm: '75%', md: '55%' }
            }}
          >
            {headerContent && (
              <Typography
                component='span'
                variant='inherit'
                className={cn(
                  'mex:!font-medium mex:mb-1 mex:flex mex:items-end mex:gap-2 mex:pt-2 mex:text-text-primary',
                  isOwn ? 'mex:flex-row-reverse' : 'mex:flex-row',
                  isCompact ? 'mex:!text-[10px]' : 'mex:!text-[12px]'
                )}
                aria-label={headerContent.ariaLabel}
              >
                {!message?.hideName && <span>{headerContent.name}</span>}
                {!message?.hideName && sentAt && (
                  <TimestampDisplay
                    timestamp={sentAt}
                    strategy={timestampStrategy}
                    className='mex:text-text-secondary'
                  />
                )}
              </Typography>
            )}
            <MessageContent
              isOwn={isOwn}
              className='mex:max-w-full'
              variant={variant}
              onContextMenu={handleContentContext}
              sxOverrides={contentSxOverrides}
            >
              <MessageListItemReply
                reply={reply}
                replyUser={replyUser || {}}
                isOwn={isOwn}
                onClick={handleReplyClick}
              />
              <MessageListItemForward msg={message} />
              {children}
            </MessageContent>

            <MessageStatusIcon status={message?.status} isSending={message?.isSending} isOwn={isOwn} isLast={isLast} />
          </Stack>
          <MessageQuickActions message={message} isOwn={isOwn} />
        </Stack>
        <MessageMenuWrapper
          isOwn={isOwn}
          revoked={message?.isRevoked}
          sending={message?.isSending}
          contextMenu={contextMenu}
          selectedMessage={selectedMessage}
          onClose={handleClose}
        />
        <MessageReactions
          className={cn(
            'mex:-translate-y-3.5 mex:z-10 mex:row-span-1 mex:row-start-3',
            isOwn ? 'mex:col-start-1' : 'mex:col-start-2'
          )}
          reactions={message?.reactions}
          conversationId={message?.conversationId || ''}
          messageId={message?.messageId || ''}
          bucketId={message?.bucketId || ''}
          messageContent={message?.content || ''}
          isOwn={isOwn}
        />
      </div>
    );
  }
);

MessageLayout.displayName = 'MessageLayout';

/**
 * Custom comparison function for React.memo
 * Only re-renders if message content/status actually changes
 * Prevents unnecessary re-renders from parent updates
 */
function areMessagePropsEqual(prevProps: MessageLayoutProps, nextProps: MessageLayoutProps): boolean {
  const prevMessage = prevProps.message;
  const nextMessage = nextProps.message;

  // Compare primitive props that affect rendering
  if (prevProps.isOwn !== nextProps.isOwn) return false;
  if (prevProps.mode !== nextProps.mode) return false;
  if (prevProps.isLast !== nextProps.isLast) return false;
  if (prevProps.avatarSize !== nextProps.avatarSize) return false;
  if (prevProps.timestampStrategy !== nextProps.timestampStrategy) return false;

  // Compare message fields that affect rendering
  if (prevMessage?.messageId !== nextMessage?.messageId) return false;
  if (prevMessage?.content !== nextMessage?.content) return false;
  if (prevMessage?.status !== nextMessage?.status) return false;
  if (prevMessage?.isRevoked !== nextMessage?.isRevoked) return false;
  if (prevMessage?.isSending !== nextMessage?.isSending) return false;
  if (prevMessage?.hideAvatar !== nextMessage?.hideAvatar) return false;
  if (prevMessage?.hideName !== nextMessage?.hideName) return false;

  // Compare reactions - shallow equality check
  const prevReactions = prevMessage?.reactions;
  const nextReactions = nextMessage?.reactions;
  if (prevReactions !== nextReactions) {
    // If both are objects, compare keys and values
    if (prevReactions && nextReactions) {
      const prevKeys = Object.keys(prevReactions);
      const nextKeys = Object.keys(nextReactions);
      if (prevKeys.length !== nextKeys.length) return false;
      for (const key of prevKeys) {
        if (prevReactions[key] !== nextReactions[key]) return false;
      }
    } else {
      return false;
    }
  }

  return true;
}

/** Memoized MessageLayout with custom comparison for performance */
export const MemoizedMessageLayout = memo<MessageLayoutProps>(
  (props) => <MessageLayout {...props} />,
  areMessagePropsEqual
);
